// Angular
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// Angular Material
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

// angulartics
import { Angulartics2 } from 'angulartics2';

// rxjs
import { Subscription } from 'rxjs';

// localforage
import * as localforage from 'localforage';

// Services
import { ApiService } from '@app/services/api/api.service';
import { IdleService } from '@app/services/idle/idle.service';

// Components
import { GuestIdleComponent } from '@app/components/guest-idle/guest-idle.component';

@Component({
  selector: 'oh-guest-sign-in',
  templateUrl: './guest-sign-in.component.html',
  styleUrls: ['./guest-sign-in.component.scss'],
})
export class GuestSignInComponent implements OnInit, OnDestroy {
  private timeoutSubscription: Subscription;
  private timeoutStartedSubscription: Subscription;
  private idleDialogRef: MatDialogRef<GuestIdleComponent>;
  private user: any;
  private token: string;
  private activeUser: any;
  public data: any;
  public form: FormGroup;
  public openHouse: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private idleService: IdleService,
    private dialog: MatDialog,
    private apiService: ApiService,
    private angulartics2: Angulartics2,
  ) {}

  get valid() {
    return this.form.valid;
  }

  get value() {
    return this.form.value;
  }

  private async getData() {
    try {
      this.data = await this.apiService.getAgentDataFromDatabase();
    } catch (error) {
      console.error(error);
    }
  }

  async ngOnInit() {
    this.idleService.startWatching();

    try {
      this.openHouse = await localforage.getItem('oh-selected-open-house');
    } catch (error) {
      console.log(error);
    }

    try {
      this.user = await localforage.getItem('oh-active-user');
    } catch (error) {
      console.log(error);
    }

    await this.getData();

    // Determine which version of the guest sign-in form to display
    switch (this.data.guest_signin.phones) {
      case "required": // Phones required
        this.form = this.fb.group({
          name: [this.user ? this.user.name : '', Validators.required],
          email: [this.user ? this.user.email : '', Validators.compose([Validators.email, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)])],
          phone: [this.user ? this.user.phone : '', [Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/), Validators.required]],
        });
        break;
      case "active": // Phones active
        this.form = this.fb.group({
          name: [this.user ? this.user.name : '', Validators.required],
          email: [this.user ? this.user.email : '', Validators.compose([Validators.email, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)])],
          phone: [this.user ? this.user.phone : '', Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)],
        });
        break;
      default: // Phones disabled
        this.form = this.fb.group({
          name: [this.user ? this.user.name : '', Validators.required],
          email: [this.user ? this.user.email : '', Validators.compose([Validators.email, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)])],
        });
    }

    this.token = await this.apiService.getTokenFromDatabase();

    this.timeoutStartedSubscription = this.idleService.onTimeoutStarted$.subscribe((value) => {
      if (value === true) {
        this.idleDialogRef = this.dialog.open(GuestIdleComponent, { width: '300px' });
      }
    });

    this.timeoutSubscription = this.idleService.onTimeout$.subscribe(async (value) => {
      if (value === true) {
        await this.submitSignInDataOnTimeout();
        this.idleDialogRef.close();
        this.router.navigate(['/welcome']);
        this.angulartics2.eventTrack.next({
          action: 'closedAndReturnedToWelcome',
          properties: { category: 'idleTimer', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
        });
      } else if (value === false) {
        if (this.idleDialogRef) {
          this.idleDialogRef.close();
          this.angulartics2.eventTrack.next({
            action: 'closed',
            properties: { category: 'idleTimer', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
          });
        }
      }
    });

    try {
      this.activeUser = await localforage.getItem('oh-active-user');
      if (!this.activeUser) {
        return;
      }
      this.form = this.fb.group({
        name: [this.activeUser.name, Validators.required],
        email: [this.activeUser.email, Validators.email],
      });
    } catch (error) {
      console.log(error);
    }
  }

  ngOnDestroy() {
    this.timeoutSubscription.unsubscribe();
    this.timeoutStartedSubscription.unsubscribe();
    this.idleService.destroy();
    if (this.idleDialogRef) {
      this.idleDialogRef.close();
    }
    this.idleDialogRef = null;
  }

  // reset idle timer when user input is detected
  @HostListener('window:keydown')
  @HostListener('window:mousemove')
  @HostListener('window:click')
  @HostListener('window:touchstart')
  @HostListener('window:touchend')
  private resetIdleTimer() {
    this.idleService.resetTimer();
    if (this.idleDialogRef) {
      this.idleDialogRef.close();
    }
  }

  private async submitSignInDataOnTimeout() {
    const submission = {
      user: this.value,
      answers: [],
    };

    if (navigator.onLine) {
      const survey: any = await localforage.getItem('oh-survey-submissions');

      // POST submissions to API and remove from cache
      if (survey) {
        for (const entry of survey) {
          await this.apiService.postSurvey(this.token, entry);
        }
        await localforage.removeItem('oh-survey-submissions');
      }
    } else {
      try {
        let submissions: any = await localforage.getItem('oh-survey-submissions');
        if (!submissions) {
          submissions = [];
        }
        submissions = [...submissions, submission];
        try {
          await localforage.setItem('oh-survey-submissions', submissions);
        } catch (error) {
          console.error(error);
        }
      } catch (error) {
        console.error(error);
      }
    }

    localforage.removeItem('oh-active-user');
  }

  public async signIn() {
    await localforage.setItem('oh-active-user', this.value);
    this.router.navigate(['/survey'], { queryParams: { question: 0 } });
    this.angulartics2.eventTrack.next({
      action: 'submitted',
      properties: { category: 'signIn', label: `open_id: ${this.openHouse.open_id} ~ online: ${navigator.onLine}` },
    });
  }
}
